import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp, resendSignUpCode } from '@aws-amplify/auth';
import TurnstileWidget from './TurnstileWidget';
import { EMAIL_LINK_OPTIONS } from './emailLinkOptions';

function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  // Honeypot: hidden from people, but simple bots fill in every field
  const [website, setWebsite] = useState('');
  const [existingUnconfirmed, setExistingUnconfirmed] = useState(false);
  const turnstileRef = useRef(null);
  const navigate = useNavigate();

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage('');
    setExistingUnconfirmed(false);
    setIsLoading(true);

    if (!turnstileToken) {
      setMessage('Please complete the verification check below.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (password.length < 8) {
      setMessage('Password must be at least 8 characters');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
          // Verify.js finishes the sign-in once the code is confirmed in this tab
          autoSignIn: true,
          ...EMAIL_LINK_OPTIONS,
          // Checked by the Cognito pre-sign-up Lambda before the account is created
          validationData: { turnstileToken, website },
        },
      });
      setMessage('Sign-up successful! Check your email for verification code.');
      setIsError(false);
      setTimeout(() => navigate('/verify', { state: { email } }), 2000);
    } catch (err) {
      console.error('Sign-up error:', err);
      if (err.name === 'UsernameExistsException') {
        // May be an account that never entered its code - let them get a new one
        setExistingUnconfirmed(true);
        setMessage('An account with this email already exists.');
      } else if (err.name === 'UserLambdaValidationException') {
        setMessage('We couldn\'t verify you\'re human. Please try the check again.');
      } else {
        setMessage(err.message || 'Sign-up failed');
      }
      setIsError(true);
      setIsLoading(false);
      turnstileRef.current?.reset();
    }
  };

  const handleResendForExisting = async () => {
    setIsLoading(true);
    try {
      await resendSignUpCode({ username: email, options: EMAIL_LINK_OPTIONS });
      navigate('/verify', { state: { email } });
    } catch (err) {
      console.error('Resend code error:', err);
      // Cognito rejects resends for already-confirmed accounts
      setExistingUnconfirmed(false);
      setMessage(
        err.name === 'InvalidParameterException'
          ? 'This account is already verified. Please sign in.'
          : err.message || 'Failed to resend code'
      );
      setIsError(true);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-quicksand flex flex-col md:flex-row">
      {/* Left Side - Branding */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-study-teal via-study-pink to-study-yellow p-12 flex-col justify-center items-center text-center">
        <img
          src="/studyPassLogo2.png"
          alt="StudyPass Logo"
          className="w-32 h-32 mb-8 rounded-full border-4 border-white shadow-lg"
        />
        <h1 className="text-4xl font-bold text-study-black mb-4">
          Start Your Journey
        </h1>
        <p className="text-xl text-study-black mb-8 max-w-md">
          Join thousands of dental hygiene students preparing for their boards with StudyPass.
        </p>

        {/* Benefits */}
        <div className="space-y-4 text-left max-w-sm">
          <div className="flex items-center gap-3 bg-white/30 rounded-xl p-3">
            <span className="text-2xl">✓</span>
            <span className="text-study-black font-semibold">Free account with 1 quiz per day</span>
          </div>
          <div className="flex items-center gap-3 bg-white/30 rounded-xl p-3">
            <span className="text-2xl">✓</span>
            <span className="text-study-black font-semibold">Track your progress over time</span>
          </div>
          <div className="flex items-center gap-3 bg-white/30 rounded-xl p-3">
            <span className="text-2xl">✓</span>
            <span className="text-study-black font-semibold">Upgrade anytime for unlimited access</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden text-center mb-8">
            <img
              src="/studyPassLogo2.png"
              alt="StudyPass Logo"
              className="w-20 h-20 mx-auto mb-4 rounded-full border-4 border-study-teal shadow-lg"
            />
          </div>

          <h2 className="text-3xl font-bold text-study-black mb-2 text-center md:text-left">
            Create Account
          </h2>
          <p className="text-gray-600 mb-8 text-center md:text-left">
            Sign up to start studying for your boards
          </p>

          <form onSubmit={handleSignUp} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-study-black mb-2">
                Email Address
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  ✉️
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-study-black mb-2">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔒
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-semibold text-study-black mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔒
                </span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className={`text-sm ${password === confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </div>
            )}

            {/* Honeypot - off-screen and skipped by keyboard/screen readers */}
            <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            {/* Bot check */}
            <TurnstileWidget
              ref={turnstileRef}
              onToken={setTurnstileToken}
              onError={() => {
                setMessage('Verification check failed to load. Please refresh the page.');
                setIsError(true);
              }}
            />

            {/* Message */}
            {message && (
              <div className={`p-4 rounded-xl text-center ${
                isError
                  ? 'bg-red-100 border border-red-300 text-red-700'
                  : 'bg-green-100 border border-green-300 text-green-700'
              }`}>
                {message}
                {existingUnconfirmed && (
                  <div className="mt-3 space-x-4">
                    <button
                      type="button"
                      onClick={handleResendForExisting}
                      className="font-bold text-study-teal hover:text-study-pink"
                    >
                      Send me a new code
                    </button>
                    <Link to="/SignIn" className="font-bold text-study-teal hover:text-study-pink">
                      Sign in
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-study-pink to-study-teal text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || !turnstileToken}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Terms */}
          <p className="text-xs text-gray-500 text-center mt-4">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-study-teal hover:underline">Terms of Use</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-study-teal hover:underline">Privacy Policy</Link>
          </p>

          {/* Divider */}
          <div className="flex items-center my-8">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-gray-500 text-sm">or</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          {/* Sign In Link */}
          <div className="text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link
                to="/SignIn"
                className="text-study-teal font-bold hover:text-study-pink transition-all"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
